import { computePackages, priceFreshness } from './allocationMath';
import { readPriceComparison } from './priceComparison.js';
import { itemCountUnitPhysicalBasis, resolveNeedToPackageBasis, validatePackageConsistency } from './packageBasis.js';
import { estimatedExtendedCost, billingBasisNeedsReview, resolveBillingBasis } from './billingBasis.js';

/**
 * Purchase-option economics — ONE view model for one option filling one need.
 *
 * Four unit concepts flow through purchasing, and every historical bug in
 * this area came from letting one field do two of these jobs:
 *
 *   inventoryQuantityUnit  the vendor-neutral unit on-hand and need are in
 *                          (bun, lb, gal). qty_per_package is the TOTAL a
 *                          package contains in THIS unit — never the number
 *                          of inner packs.
 *   countFormatUnit        what staff physically count (Costco case, loose
 *                          pack). Lives in count presentation, not here.
 *   orderUnit              what the vendor sells (case, bag). purchase_unit.
 *   comparisonUnit         one practical unit for the whole relationship
 *                          (bun, lb, gal) that makes two vendors' prices
 *                          comparable. May differ from the inventory unit
 *                          (inventory fl oz, compared per gal).
 *
 * Two prices come out, and they are different questions:
 *
 *   orderCost           packagesRequired × packagePrice — what covering the
 *                       need costs today. Drives recommendations.
 *   comparableUnitCost  packagePrice ÷ comparisonQuantityPerPackage,
 *                       UNROUNDED — what one comparison unit costs inside
 *                       the package. Shown beside orderCost, never silently
 *                       substituted for it.
 *
 * Every surface that shows an option filling a need — Savings Review, vendor
 * picker, related products, options editor, PO review, shopping lists —
 * renders from this object. No component computes package contents, unit
 * cost, order quantity or overage on its own.
 */

const toFiniteNumber = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toPositiveNumber = (value) => {
  const parsed = toFiniteNumber(value);
  return parsed !== null && parsed > 0 ? parsed : null;
};

const roundMoney = (value) => Math.round(value * 100) / 100;

/**
 * The package's total quantity in the inventory quantity unit, with its
 * provenance. Order of trust:
 *
 *   1. a human-confirmed package interpretation in conversion_snapshot;
 *   2. structured inner-pack columns (count × size);
 *   3. the stored qty_per_package column.
 *
 * Never re-parses pack text here — parsing happens once, server-side, and is
 * frozen into the snapshot. A parser improving must not change what a
 * confirmed package means.
 */
export function resolvePackageQuantity(option, { item = null } = {}) {
  // Delegates to the ONE canonical resolver (shared with the edge functions),
  // keeping this module's historical return shape. A package whose stored
  // facts conflict — qty_per_package vs inner-pack structure vs the
  // deterministic comparison — resolves to NO quantity: there is no single
  // package the number could describe, and picking one silently is the bug
  // this resolver exists to prevent.
  const check = validatePackageConsistency(option, { item });
  if (check.status === 'conflict') {
    return { quantity: null, unit: check.basis.inventoryUnit, source: 'conflict', requiresReview: true };
  }
  const basis = check.basis;
  return {
    quantity: basis.quantityInInventoryUnit,
    unit: basis.inventoryUnit,
    source: basis.source,
    requiresReview: basis.requiresReview,
  };
}

/**
 * Builds the complete economics of one option covering one need.
 *
 * @param {object} args
 * @param {object} args.option        purchase_options row (with snapshot)
 * @param {number} args.needQty       vendor-neutral need, inventory unit
 * @param {string} [args.inventoryQuantityUnit] the item's unit, for labels
 * @param {object} [args.comparison]  assembled relationship comparison for
 *                                    the item ({ comparisonUnit, options })
 * @param {object} [args.vendorsById] vendor id → row, for names
 * @param {Date|number} [args.now]
 */
export function buildPurchaseOptionEconomics({
  option,
  needQty = 0,
  inventoryQuantityUnit = null,
  comparison = null,
  vendorsById = {},
  item = null,
  now = Date.now(),
  // The item's count-unit physical definition (itemCountUnitPhysicalBasis),
  // when the caller has the item's own options — lets a package measured only
  // in fl oz cover a case-counted need. buildOptionSetEconomics derives it.
  needBridge = null,
} = {}) {
  if (!option) return null;

  const warnings = [];
  const assumptions = [];
  const packagePrice = toPositiveNumber(option.package_price);
  const consistency = validatePackageConsistency(option, {
    item: item ?? (inventoryQuantityUnit ? { unit: inventoryQuantityUnit } : null),
    needBridge,
  });
  const pkg = resolvePackageQuantity(option, {
    item: item ?? (inventoryQuantityUnit ? { unit: inventoryQuantityUnit } : null),
  });
  const resolvedNeedUnit = inventoryQuantityUnit
    ?? item?.unit
    ?? item?.count_unit
    ?? pkg.unit
    ?? option.normalized_count_unit
    ?? null;

  if (packagePrice === null) warnings.push('missing_price');
  if (consistency.status === 'conflict') {
    // The row's stored facts describe different packages. Every consistency
    // code rides along so the card can explain WHICH facts disagree.
    warnings.push(...consistency.warnings.filter((w) => !warnings.includes(w)));
  }
  if (pkg.quantity === null && consistency.status !== 'conflict') warnings.push('unresolved_conversion');
  else if (pkg.quantity !== null && pkg.requiresReview) warnings.push('needs_unit_review');
  // ── Order math, inventory quantity unit ────────────────────────────────
  const need = toFiniteNumber(needQty) || 0;
  const needBasis = resolveNeedToPackageBasis({
    needQty: need,
    needUnit: resolvedNeedUnit,
    option,
    packageBasis: consistency.basis,
    item: item ?? (inventoryQuantityUnit ? { unit: inventoryQuantityUnit } : null),
    needBridge,
  });
  if (!needBasis.compatible) {
    warnings.push('need_unit_incompatible_with_package_basis');
  }
  const packageMath = needBasis.compatible
    ? computePackages({
      needQty: needBasis.convertedNeedQty,
      qtyPerPackage: needBasis.quantityPerPackageInNeedUnit,
      moqPackages: option.moq_packages,
      allowsFractional: option.allows_fractional_packages === true,
      orderIncrement: option.order_increment,
    })
    : { packages: null, quantityReceived: null, overage: null, driver: null };
  const { packages, quantityReceived, overage, driver } = packageMath;
  const exactOrderCost = packagePrice !== null && packages !== null
    ? roundMoney(packages * packagePrice)
    : null;

  // ── ORDER is not BILLING ────────────────────────────────────────────────
  //
  // A catch-weight case is ordered by the case and invoiced by the pound, so
  // packages × packagePrice is not its cost — $16.40 for a ~60 LB ribeye case
  // is the per-pound rate wearing a line total's clothes. When the row proves
  // that shape, orderCost becomes packages × est. weight × rate and says it is
  // an estimate. Nothing else moves: packagesRequired, quantityReceived and
  // overage still come from computePackages, and comparableUnitCost below is
  // still packagePrice ÷ comparisonQuantityPerPackage.
  const billingEstimate = estimatedExtendedCost(option, packages, {
    item: item ?? (inventoryQuantityUnit ? { unit: inventoryQuantityUnit } : null),
  });
  const orderCost = billingEstimate ? billingEstimate.amount : exactOrderCost;
  if (billingEstimate) warnings.push('cost_is_estimated');

  // A row whose pack SAYS the weight varies but cannot say by how much keeps
  // today's price untouched and is flagged, never silently re-priced.
  const billingBasis = resolveBillingBasis(option, {
    item: item ?? (inventoryQuantityUnit ? { unit: inventoryQuantityUnit } : null),
  });
  const billingNeedsReview = billingBasisNeedsReview(billingBasis);
  if (billingNeedsReview) warnings.push('billing_basis_needs_review');

  // ── Comparable price, relationship comparison unit, UNROUNDED ──────────
  // From the stored relationship snapshot when present; the entry list of an
  // assembled comparison when supplied; never from the 6dp-rounded
  // normalized_unit_cost column — write-time rounding must not decide ties.
  let comparisonUnit = comparison?.comparisonUnit ?? null;
  let comparisonQuantityPerPackage = null;
  let comparisonSource = null;
  let comparisonConfidence = null;
  let estimated = false;
  let comparisonBasisVerified = true;

  // A comparison may only speak for the package the order math is buying.
  // Assembled entries were already validated against the basis when the
  // comparison was assembled; a raw stored snapshot is validated here. A
  // conflicted option gets NO comparable price — there is no agreed package
  // for the price to describe.
  if (consistency.status !== 'conflict') {
    const entry = (comparison?.options || []).find(
      (candidate) => String(candidate.optionId) === String(option.id),
    );
    if (entry) {
      comparisonQuantityPerPackage = toPositiveNumber(entry.quantityPerPurchasePackage);
      comparisonSource = entry.source ?? null;
      comparisonConfidence = entry.confidence ?? null;
      estimated = entry.estimated === true || entry.source === 'ai_estimate';
      comparisonBasisVerified = entry.basisVerified !== false;
      if (entry.assumption) assumptions.push(entry.assumption);
    } else {
      const stored = readPriceComparison(option);
      if (stored && consistency.comparisonUsable) {
        comparisonUnit = comparisonUnit ?? stored.relationshipUnit ?? null;
        comparisonQuantityPerPackage = toPositiveNumber(stored.quantityPerPurchasePackage);
        comparisonSource = stored.source ?? null;
        comparisonConfidence = stored.confidence ?? null;
        estimated = stored.source === 'ai_estimate';
        comparisonBasisVerified = consistency.comparisonReady;
        if (stored.assumption) assumptions.push(stored.assumption);
      }
    }
  }

  const comparableUnitCost = packagePrice !== null && comparisonQuantityPerPackage !== null
    ? packagePrice / comparisonQuantityPerPackage
    : null;
  if (comparableUnitCost === null && pkg.quantity !== null) warnings.push('comparison_needs_review');
  if (estimated) warnings.push('estimated_conversion');

  const freshness = priceFreshness(option.effective_date, new Date(now));
  if (freshness.stale) warnings.push('stale_price');

  const vendor = option.vendor_id != null ? vendorsById[option.vendor_id] || null : null;

  return {
    optionId: String(option.id),
    vendorId: option.vendor_id ?? null,
    vendorName: vendor?.display_name || option.vendor_name_text || null,
    vendorProductDescription: option.vendor_description ?? null,

    purchaseUnit: option.purchase_unit ?? null,
    packDescription: option.pack_description ?? null,
    packagePrice,

    inventoryQuantityUnit: resolvedNeedUnit,
    inventoryQuantityPerPackage: needBasis.quantityPerPackageInNeedUnit,

    comparisonUnit,
    comparisonUnitLabel: comparisonUnit ? `Compared per ${comparisonUnit}` : null,
    comparisonQuantityPerPackage,
    comparableUnitCost,

    packagesRequired: packages,
    quantityReceived,
    overage,
    // Why packagesRequired is what it is (ORDER_DRIVERS) — straight from
    // computePackages; null when the need unit is incompatible. Vendor-layer
    // only: economics never applies item replenishment rules.
    orderDriver: driver ?? null,
    orderCost,
    // Two facts a caller needs before it may print orderCost as money:
    // whether the vendor settles this line on a scale, and on what basis.
    // "estimated — final invoice varies with shipped weight" is the label.
    costIsEstimated: billingEstimate !== null,
    billingBasisText: billingEstimate ? billingEstimate.basisText : null,
    fulfillmentModel: billingBasis.model,
    billingUnit: billingBasis.billingUnit,
    billingUnitPrice: billingBasis.billingUnitPrice,
    estimatedBillingQtyPerOrderUnit: billingBasis.estimatedBillingQtyPerOrderUnit,
    billingBasisNeedsReview: billingNeedsReview,
    orderReady: consistency.orderReady && needBasis.compatible,
    needConversionSource: needBasis.source,
    packageMeasurements: needBasis.measurements,

    moqPackages: toPositiveNumber(option.moq_packages) ?? 1,

    packageInterpretationSource: pkg.source,
    comparisonSource,
    comparisonBasisVerified,
    confidence: comparisonConfidence,
    estimated,
    assumptions,
    warnings,

    priceFreshness: freshness,
    // Relative flags — the caller sets these across the option set.
    isLowestOrderCost: false,
    isBestComparableUnitPrice: false,
  };
}

/**
 * Economics for every option of one item against one need, with the two
 * titles awarded across the set. They routinely go to different vendors —
 * that divergence is the whole point of showing both.
 *
 * A low-confidence AI estimate may hold "best unit price" for display, but
 * `trustworthyBestUnitPrice` refuses it — an estimate below the confidence
 * bar must not crown a winner.
 */
export function buildOptionSetEconomics({
  options = [],
  needQty = 0,
  inventoryQuantityUnit = null,
  comparison = null,
  vendorsById = {},
  item = null,
  now = Date.now(),
  aiConfidenceThreshold = 0.6,
} = {}) {
  // One derivation for the whole set: what one count unit of this item
  // physically contains, from the item's own packages.
  const needBridge = itemCountUnitPhysicalBasis({
    item: item ?? (inventoryQuantityUnit ? { unit: inventoryQuantityUnit } : null),
    options,
  });
  const rows = options
    .map((option) => buildPurchaseOptionEconomics({
      option, needQty, inventoryQuantityUnit, comparison, vendorsById, item, now, needBridge,
    }))
    .filter(Boolean);

  const costs = rows.map((r) => r.orderCost).filter((c) => c !== null);
  const lowestOrder = costs.length ? Math.min(...costs) : null;
  const unitCosts = rows
    .filter((r) => r.orderReady)
    .map((r) => r.comparableUnitCost)
    .filter((c) => c !== null);
  const bestUnit = unitCosts.length ? Math.min(...unitCosts) : null;

  const trustworthy = rows.filter((r) =>
    r.orderReady
    && r.comparableUnitCost !== null
    && r.comparisonBasisVerified !== false
    && (!r.estimated || (r.confidence !== null && r.confidence >= aiConfidenceThreshold)));
  const trustworthyBest = trustworthy.length
    ? Math.min(...trustworthy.map((r) => r.comparableUnitCost))
    : null;

  return rows.map((row) => ({
    ...row,
    isLowestOrderCost: lowestOrder !== null && row.orderCost === lowestOrder,
    isBestComparableUnitPrice: bestUnit !== null && row.comparableUnitCost === bestUnit,
    trustworthyBestUnitPrice: trustworthyBest !== null
      && row.comparableUnitCost === trustworthyBest
      && (!row.estimated || (row.confidence !== null && row.confidence >= aiConfidenceThreshold)),
    excessCost: lowestOrder !== null && row.orderCost !== null
      ? roundMoney(row.orderCost - lowestOrder)
      : null,
  }));
}

/* ── Vendor / landed economics ──────────────────────────────────────────── */

/**
 * Charges one vendor order actually incurs, from the vendor's purchasing
 * profile. A hard minimum is a feasibility problem, not a fee — it comes
 * back as `feasible: false` with the shortfall, never as money added.
 *
 * The fuel surcharge rides the truck: a pickup order never pays it, an
 * unknown amount (NULL — not the same claim as $0) charges nothing, and a
 * vendor that waives delivery above free_delivery_threshold waives the
 * surcharge with it.
 */
export function vendorOrderCharges({ vendor = null, subtotal = 0 } = {}) {
  const minimum = toPositiveNumber(vendor?.minimum_order_amount);
  const threshold = toPositiveNumber(vendor?.free_delivery_threshold);
  const deliveryFeeRaw = toFiniteNumber(vendor?.delivery_fee);
  const fuelSurchargeRaw = toFiniteNumber(vendor?.fuel_surcharge_amount);
  const belowMinFee = toPositiveNumber(vendor?.below_minimum_fee);
  const isPickup = (vendor?.fulfillment_type || 'delivery') === 'pickup';

  const underMinimum = minimum !== null && subtotal < minimum;
  const deliveryFee = isPickup || deliveryFeeRaw === null ? 0
    : (threshold !== null && subtotal >= threshold ? 0 : Math.max(0, deliveryFeeRaw));
  // Same waiver rules as the delivery fee, deliberately: the surcharge exists
  // because a truck comes, so it disappears exactly when the delivery fee does.
  const fuelSurcharge = isPickup || fuelSurchargeRaw === null ? 0
    : (threshold !== null && subtotal >= threshold ? 0 : Math.max(0, fuelSurchargeRaw));
  // A vendor that charges for small orders rather than refusing them stays
  // feasible at a price; one with a bare minimum does not.
  const belowMinimumFee = underMinimum && belowMinFee !== null ? belowMinFee : 0;
  const feasible = !underMinimum || belowMinFee !== null;

  return {
    minimumOrderAmount: minimum,
    minimumMet: minimum === null ? null : !underMinimum,
    shortfall: underMinimum ? roundMoney(minimum - subtotal) : null,
    deliveryFee: roundMoney(deliveryFee),
    fuelSurcharge: roundMoney(fuelSurcharge),
    belowMinimumFee: roundMoney(belowMinimumFee),
    totalCharges: roundMoney(deliveryFee + fuelSurcharge + belowMinimumFee),
    feasible,
    landedTotal: roundMoney(subtotal + deliveryFee + fuelSurcharge + belowMinimumFee),
  };
}

/**
 * Landed totals for a whole plan: product subtotal + every applicable fee.
 *
 * Two savings figures, deliberately named apart:
 *   productSavings  current product subtotal − cheapest product subtotal
 *   landedSavings   current landed total − cheapest FEASIBLE landed total
 * The recommendation uses landedSavings; "moving one item saves $3" must not
 * survive the $20 delivery fee it creates.
 */
export function landedPlanTotals(vendorOrders = []) {
  let productSubtotal = 0;
  let fees = 0;
  let infeasibleCount = 0;
  // How much of the subtotal is a catch-weight ESTIMATE. Carried up to the
  // plan so no surface can present a landed total as exact when part of it is
  // settled on a scale — the caller decides the wording, but it cannot claim
  // ignorance.
  let estimatedSubtotal = 0;
  let estimatedLineCount = 0;
  const orders = vendorOrders.map((order) => {
    const charges = vendorOrderCharges({ vendor: order.vendor, subtotal: order.subtotal });
    productSubtotal += order.subtotal || 0;
    estimatedSubtotal += order.estimatedSubtotal || 0;
    estimatedLineCount += order.estimatedLineCount || 0;
    fees += charges.totalCharges;
    if (!charges.feasible) infeasibleCount += 1;
    return { ...order, charges };
  });
  return {
    orders,
    productSubtotal: roundMoney(productSubtotal),
    fees: roundMoney(fees),
    landedTotal: roundMoney(productSubtotal + fees),
    estimatedSubtotal: roundMoney(estimatedSubtotal),
    estimatedLineCount,
    containsEstimatedCosts: estimatedLineCount > 0,
    feasible: infeasibleCount === 0,
    infeasibleCount,
  };
}
